import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AlgoMode,
  CampaignType,
  Intensity,
  Marketplace,
  MatchType,
  StrategyTactic,
  TacticType,
} from "@front/m19-api-client";
import { AdvertisingStrategyEx, SegmentConfigType, SegmentEx } from "@front/m19-models";
import { AccountSelectionService, SegmentService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  IIconComponent,
  IIntensityStepperComponent,
  IIntensityStepperOption,
  IToggleComponent,
  ITooltipDirective,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { KeywordSegmentModalComponent } from "@m19-board/segments/keyword-segment-modal/keyword-segment-modal.component";
import {
  ProductSegmentModalComponent,
  ProductSegmentModalData,
} from "@m19-board/segments/product-segment-modal.component";
import { startWith, switchMap, take } from "rxjs";
import { StrategyService } from "@front/m19-injection-token";
import { RemoveTacticModalComponent } from "@m19-board/strategies/tactic/remove-tactic-modal.component";

export interface RemoveTacticModalData {
  accountId: string;
  marketplace: Marketplace;
  strategyId: number;
  tactic: StrategyTactic;
  segment: SegmentEx;
  isSdTargeting: boolean;
  campaignType: CampaignType;
}

/* Main Component*/
@Component({
  selector: "app-tactic",
  templateUrl: "./tactic.component.html",
  imports: [
    TranslocoDirective,
    IBadgeComponent,
    IIconComponent,
    ITooltipDirective,
    IButtonDirective,
    IToggleComponent,
    IIntensityStepperComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacticComponent {
  private readonly modalService = inject(ModalService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyService = inject(StrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly segmentService = inject(SegmentService);

  protected readonly AlgoMode = AlgoMode;
  protected readonly SegmentType = SegmentConfigType;

  public readonly tactic = input.required<StrategyTactic>();
  // TODO: use input signal and computed instead of setters
  @Input() showKW = true;
  @Input() showProducts = true;
  @Input() isReadOnly = false;

  @Input()
  set strategy(strategy: AdvertisingStrategyEx) {
    this._strategy.set(strategy);
    this.boostTopSearchEnabled.set(strategy?.campaignType == CampaignType.SP);
  }

  @Output() tacticDeleted = new EventEmitter<void>();

  readonly intensityOptions: IIntensityStepperOption<Intensity>[] = [
    {
      value: Intensity.LOW,
      label: this.translocoService.translate("common.lowest"),
    },
    {
      value: Intensity.MEDIUM_LOW,
      label: this.translocoService.translate("common.lower"),
    },
    {
      value: Intensity.NEUTRAL,
      label: this.translocoService.translate("common.neutral"),
    },
    {
      value: Intensity.MEDIUM_HIGH,
      label: this.translocoService.translate("common.higher"),
    },
    {
      value: Intensity.HIGH,
      label: this.translocoService.translate("common.highest"),
    },
  ];

  protected readonly segmentType = computed<SegmentConfigType | undefined>(() => {
    return this.segment()?.segmentType;
  });

  protected readonly _strategy = signal<AdvertisingStrategyEx | undefined>(undefined);
  protected readonly boostTopSearchEnabled = signal<boolean>(false);
  protected readonly intensity = computed<Intensity>(() => {
    return this.tactic().intensity ?? Intensity.NEUTRAL;
  });
  private readonly segmentIndex = toSignal<Map<number, SegmentEx>>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      startWith(new Map()),
    ),
    { requireSync: true, equal: () => false },
  );
  public readonly segment = computed<SegmentEx | undefined>(
    () => {
      return this.segmentIndex().get(this.tactic().segmentId!);
    },
    { equal: () => false },
  );

  protected readonly getSegmentName = computed<string>(() => {
    return this.segment()?.name ?? "";
  });

  protected getSegmentTypeLabel = computed(() => {
    switch (this.segmentType()) {
      case SegmentConfigType.KeywordSegment:
        return this.translocoService.translate("common.keyword");
      case SegmentConfigType.ProductSegment:
        return this.translocoService.translate("common.product");
      default:
        return "" + this.segmentType()?.toString();
    }
  });

  protected getSegmentTypeDesc = computed(() => {
    switch (this.segmentType()) {
      case SegmentConfigType.KeywordSegment:
        return this.translocoService.translate("tactic.excludes_kw_from_your_strategy");
      case SegmentConfigType.ProductSegment:
        return this.translocoService.translate("tactic.excludes_product_pages_from_your_strategy");
      case SegmentConfigType.MixedSegment:
      default:
        return this.translocoService.translate("tactic.excludes_product_pages_kw_from_your_strategy");
    }
  });

  protected nbKeywords = computed<number | undefined>(() => {
    return this.segment()?.items.filter((x) => x.matchType == MatchType.exact || x.matchType == MatchType.phrase)
      .length;
  });

  protected nbProductTargeting = computed<number | undefined>(() => {
    return this.segment()?.items.filter((x) => x.matchType == MatchType.asinSameAs).length;
  });

  protected lastTacticForTargeting = computed<boolean>(() => {
    const strategy = this._strategy();
    return (
      strategy !== undefined &&
      this.tactic().tacticType != TacticType.BLACKLIST &&
      strategy.tactics.filter(
        (x) =>
          x.tacticType != TacticType.BLACKLIST &&
          ((strategy.isSpStrategy() && strategy.productTargetingEnabled) ||
            (this.segmentType() === SegmentConfigType.KeywordSegment &&
              this.segmentIndex().get(x.segmentId)?.segmentType !== SegmentConfigType.ProductSegment)),
      ).length == 1
    );
  });

  protected changeIntensity(intensity: Intensity) {
    if (intensity !== this.tactic().intensity) {
      this.strategyService
        .updateTacticIntensity(
          this._strategy()!.accountId,
          this._strategy()!.marketplace,
          this._strategy()!.strategyId,
          this.tactic().segmentId,
          intensity,
        )
        .subscribe({
          next: () => {
            this.toastrService.success(this.translocoService.translate("tactic.intensity_updated_successfully"));
          },
          error: (error) => {
            this.toastrService.error(this.translocoService.translate("tactic.error_updating_intensity_error", [error]));
          },
        });
    }
  }

  protected switchBoostPlacementTop(): void {
    this.tactic().boostPlacementTop = !this.tactic().boostPlacementTop;
    this.strategyService
      .updateTacticBoostPlacementTop(
        this._strategy()!.accountId,
        this._strategy()!.marketplace,
        this._strategy()!.strategyId,
        this.tactic().segmentId,
        this.tactic().boostPlacementTop!,
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          if (this.tactic().boostPlacementTop) {
            this.toastrService.success(
              this.translocoService.translate("tactic.only_top_of_search_placement_activated"),
            );
          } else {
            this.toastrService.success(
              this.translocoService.translate("tactic.only_top_of_search_placement_deactivated"),
            );
          }
        },
        error: (error) => {
          this.toastrService.error(
            error,
            this.translocoService.translate("tactic.error_updating_top_of_search_placement_boost"),
          );
        },
      });
  }

  protected editSegment() {
    if (this.segmentType() === SegmentConfigType.KeywordSegment) {
      this.modalService.openModal(KeywordSegmentModalComponent, {
        data: {
          accountId: this._strategy()!.accountId,
          marketplace: this._strategy()!.marketplace,
          segment: this.segment(),
          isReadOnly: this.isReadOnly,
          initSegmentItems: this.segment()?.items,
        },
        modalTitle: this.translocoService.translate("keyword-segment-modal.edit_keyword_segment"),
      });
    } else if (this.segmentType() === SegmentConfigType.ProductSegment) {
      this.modalService.openModal<ProductSegmentModalData, void>(ProductSegmentModalComponent, {
        modalTitle: `${this.translocoService.translate("common.edit")} ${this.translocoService.translate("product-segment-modal.product_segment")}`,
        data: {
          accountId: this._strategy()!.accountId,
          marketplace: this._strategy()!.marketplace,
          segment: this.segment(),
          isReadOnly: this.isReadOnly,
        },
      });
    }
  }

  protected removeTacticFromStrategy() {
    this.modalService
      .openModal<RemoveTacticModalData, number>(RemoveTacticModalComponent, {
        modalTitle: this.translocoService.translate("remove-tactic-popup.remove_tactic_from_strategy"),
        data: {
          accountId: this._strategy()!.accountId!,
          marketplace: this._strategy()!.marketplace!,
          strategyId: this._strategy()!.strategyId!,
          tactic: this.tactic()!,
          segment: this.segment()!,
          isSdTargeting: false,
          campaignType: this._strategy()!.campaignType!,
        },
      })
      .afterClosed()
      .subscribe(() => this.tacticDeleted.emit());
  }
}
